export interface PaymentRequestPayload {
  sendToEmail: string;
  sendMethod?: 'email' | 'shareable_link';
  paymentAction: 'payment' | 'deposit';
  requestedAmount: number;
  integrationKey: string;
  currency: string;
  location: Location;
  customer: Customer;
  note: string;
  documentRefs: string[];
  registerRef: string;
  shopRef: string;
  employeeRef: string;
  accountRef: string;
  pricingTables: PricingTable[];
  metadata?: Record<string, string>;
}

export interface PaymentRequestListResponse {
  data: PaymentRequestResponse[];
  meta: ResponseMeta;
}

export interface PaymentRequestResponse {
  id: string;
  status: string;
  requestedAmount: number;
  currency: string;
  location: Location;
  customer: Customer;
  contactId: string;
  note?: string;
  documentRefs: string[];
  shopRef: string;
  registerRef: string;
  accountRef: string;
  pricingTables: PricingTable[];
  metadata: Record<string, string>;
  proposalHash: string;
  proposalUrl: string;
  payments: PaymentResponse[];
}

export interface PaymentResponse {
  paymentId: string;
  status: string;
  capturedAt: string;
  chargeId: string;
  amount: number;
  currency: string;
}

export interface ResponseMeta {
  totalCount: number;
  totalAmount: number;
}

export interface Location {
  locationRef: string;
  name: string;
  email: string;
}

export interface Customer {
  customerRef: string;
  creditAccountRef: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface PricingTable extends Totals {
  currency: string;
  title: string;
  lineItems: LineItem[];
  workOrders: WorkOrder[];
  taxes: Tax[];
  discounts: Discount[];
  fees: Fee[];
}

export interface WorkOrder {
  saleLineRef: string;
  workOrderRef: string;
  workOrderType: string;
  workOrderNote: string;
}

export interface Totals {
  totalAmount: number;
  subtotal: number;
  discountTotal: number;
  feeTotal: number;
  taxTotal: number;
}

export interface Tax {
  taxRef: string;
  name: string; // LSR Tax class name ex: Item or Labor
  totalAmount: number;
  rate?: number;
  type: string; // Tax name ex: GST or PST
}

export interface LineItem extends Totals {
  lineItemRef: string;
  quantity: number;
  unitAmount: number;
  name: string;
  description: string;
  taxes: Tax[];
  discounts: Discount[];
  fees: Fee[];
  workOrderRef?: string;
}

export interface Discount {
  discountRef: string;
  name: string;
  unitAmount: number;
  totalAmount: number;
  percent: number;
  type: DiscountType;
}

export interface Fee {
  feeRef: string;
  name: string;
  totalAmount: number;
}

export type PricingTableType = 'Sale' | 'Layaway' | 'Special order' | 'Workorder' | 'Quote';

export const PricingTableTypes = {
  SALE: 'Sale' as PricingTableType,
  LAYAWAY: 'Layaway' as PricingTableType,
  SPECIAL_ORDER: 'Special order' as PricingTableType,
  WORKORDER: 'Work order' as PricingTableType,
  QUOTE: 'Quote' as PricingTableType,
};

export type PaymentRequestStatus = 'sent' | 'paid';

export const PaymentRequestStatus = {
  SENT: 'sent' as PaymentRequestStatus,
  PAID: 'paid' as PaymentRequestStatus,
};

export type PaymentRequestFilters = {
  documentRef?: number;
  customerRef: number;
  status?: string;
};

export enum DiscountType {
  PERCENT = 'percent',
  FIXED = 'fixed',
}

export type PaymentRequestSearchFilters = {
  status?: string;
  customerName?: string;
  createdAfter?: string;
  createdBefore?: string;
  sort?: string;
  page?: number;
  limit?: number;
  cursor?: string;
  includeExpired?: boolean;
  customerRef?: string;
  accountRef?: string;
};

// Single Item representation of the payment request, returned from searchPaymentRequests
export interface PaymentRequestSearchItem {
  id: string;
  status: string;
  requestedAmount: number;
  currency: string;
  accountRef: string;
  customerRef: string;
  creditAccountRef?: string;
  firstName: string;
  lastName: string;
  email: string;
  locationRef: string;
  locationName: string;
  collectableUntil?: string;
  createdAt?: string;
  paidAt?: string | null;
  proposalUrl?: string;
  paymentChargeId?: string;
}

export interface PaymentRequestSearchResponse {
  data: PaymentRequestSearchItem[];
  metadata?: {
    pagination?: {
      page?: number;
      limit?: number;
      totalCount?: number;
    };
  };
}

/**
 * DTO type used by the invoicing MFE PaymentRequestModal.
 * This is the shape expected by the modal component for displaying and editing payment requests.
 */
export interface PaymentRequestDto {
  id?: string;
  status?: string;
  requestedAmount: number;
  currency?: string;
  location: Location;
  customer: Customer;
  contactId?: string;
  note: string;
  documentRefs: string[];
  shopRef: string;
  registerRef: string;
  accountRef: string;
  pricingTables: PricingTable[];
  meta?: Record<string, string>;
  proposalHash?: string;
  proposalUrl?: string;
  payments?: PaymentResponse[];
}
